import { A, G } from '@mobily/ts-belt';
import { subDays } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FileField,
  RecordContextProvider,
  required,
  SelectInput,
  TextInput,
  useGetOne,
  useTranslate,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { DOCUMENTS_UPLOAD_LARGE_FILE_MAX_SIZE } from '@Common/constants/documents';
import { B2bSavingPlanCategory } from '@Common/types/b2b/plans';
import { GetB2bCompaniesOneResponse } from '@Common/types/bo/b2b/companies';
import { GetB2bEmployeesOneResponse } from '@Common/types/bo/b2b/employees';
import { GetEmployeeLockedFundsResponse } from '@Common/types/bo/b2b/employees/locked_funds';
import { PostB2bWithdrawalEligibleAllocationsEndpoint } from '@Common/types/bo/b2b/withdrawals';

import { b2bWithdrawalEarlyReasonChoices as b2bWithdrawalEarlyReasonChoicesRaw } from '@sh4res/bo-common/choices/withdrawals';
import { FileInput, ShowSection, TypographyTranslated } from '@sh4res/bo-common/components';
import {
  RadioButtonGroupInput,
  RadioButtonGroupInputChoices,
} from '@sh4res/bo-common/components/RadioButtonGroupInput';
import { B2bCompanySelectInput, B2bEmployeeSelectInput } from '@sh4res/bo-common/fields';
import { DateInput } from '@sh4res/bo-common/fields/DateInput';
import { useCreatePromise, useResourceBasePath, useWatchObject } from '@sh4res/bo-common/hooks';
import { DuplicateEmployeeNameAlert } from '@sh4res/bo-common/resources/b2b/withdrawals/components/DuplicateEmployeeNameAlert';
import { SuspendedFundsNotice } from '@sh4res/bo-common/resources/b2b/withdrawals/components/SuspendedFundsNotice';
import { B2bWithdrawalFormValues } from '@sh4res/bo-common/resources/b2b/withdrawals/types';
import { inputDisabledProps } from '@sh4res/bo-common/utils';
import { toDateString } from '@sh4res/bo-common/utils/date';
import { isBoExternal } from '@sh4res/bo-common/utils/env';
import { maxDate } from '@sh4res/bo-common/validators/date';

const testId = 'BoExternalB2bWithdrawalsCreateGeneralInfoStep';

const b2bWithdrawalEarlyReasonChoices = b2bWithdrawalEarlyReasonChoicesRaw.filter(
  (row) => !A.includes(['instrument_maturity', 'indirect_management'], row.id),
);

type EligibleAllocationsRequestBody = {
  employee_id: number;
  company_id?: number;
  products: B2bSavingPlanCategory[];
  early_event_date: string;
  early_reason: string | null;
};

type GeneralInfoStepProps = {
  b2bEmployee: GetB2bEmployeesOneResponse | null;
  setB2bEmployee: React.Dispatch<React.SetStateAction<GetB2bEmployeesOneResponse | null>>;
};

export type From = 'early' | 'available';

export const GeneralInfoStep = ({ b2bEmployee, setB2bEmployee }: GeneralInfoStepProps) => {
  const translate = useTranslate();
  const { setValue } = useFormContext();
  const resourceBasePath = useResourceBasePath();

  const { b2b_company_id: companyId, b2b_employee_id: employeeId } =
    useWatchObject<B2bWithdrawalFormValues>()(['b2b_company_id', 'b2b_employee_id']);

  const from: From | null = useWatch({
    name: 'from',
    defaultValue: null,
  });

  const earlyReason = useWatch({ name: 'early_reason', defaultValue: null });
  const dateOfEvent = useWatch({ name: 'date_of_event', defaultValue: null });

  const { data: company } = useGetOne<GetB2bCompaniesOneResponse>(
    `${resourceBasePath}companies`,
    {
      id: companyId ?? 0,
    },
    {
      enabled: G.isNotNullable(companyId),
    },
  );

  const isInternationalCompany = company?.is_international ?? false;

  const { data: lockedFunds } = useGetOne<GetEmployeeLockedFundsResponse>(
    `${resourceBasePath}employees/locked_funds`,
    {
      id: employeeId ?? 0,
    },
    {
      enabled: G.isNotNullable(employeeId),
    },
  );

  // Fetch eligible allocations for early withdrawals
  const [create] = useCreatePromise<
    EligibleAllocationsRequestBody,
    PostB2bWithdrawalEligibleAllocationsEndpoint['Client']['ResponseBody']
  >();
  const [eligibleAllocations, setEligibleAllocations] = useState<
    PostB2bWithdrawalEligibleAllocationsEndpoint['Client']['ResponseBody'] | null
  >(null);

  useEffect(() => {
    let isCancelled = false;

    const fetchEligibleAllocations = async () => {
      setValue('allocations', null);
      if (
        from !== 'early' ||
        G.isNullable(employeeId) ||
        G.isNullable(earlyReason) ||
        G.isNullable(dateOfEvent) ||
        G.isNullable(lockedFunds)
      ) {
        return;
      }

      try {
        const products: B2bSavingPlanCategory[] = lockedFunds.categories.map(
          (category) => category.type,
        );

        const eventDate = new Date(dateOfEvent);

        const requestBody: EligibleAllocationsRequestBody = {
          employee_id: employeeId,
          products,
          early_event_date: eventDate.toISOString(),
          early_reason: earlyReason,
        };

        if (G.isNotNullable(companyId)) {
          requestBody.company_id = companyId;
        }

        const result = await create(`${resourceBasePath}withdrawals/eligible_allocations`, {
          data: requestBody,
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- test-only escape hatch set on `window`
        if ((window as any).__SKIP_ALLOCATION_CHECK__) {
          // eslint-disable-next-line no-console -- for debugging
          console.log('Skipping allocation check');
          return;
        }

        if (!isCancelled) {
          setEligibleAllocations(result);
        }
      } catch (err) {
        // eslint-disable-next-line no-console -- for debugging
        console.error('Failed to fetch eligible allocations:', err);
        if (!isCancelled) {
          setEligibleAllocations(null);
        }
      }
    };

    void fetchEligibleAllocations();

    return () => {
      isCancelled = true;
    };
  }, [
    from,
    employeeId,
    companyId,
    earlyReason,
    dateOfEvent,
    create,
    lockedFunds,
    setValue,
    resourceBasePath,
  ]);

  useEffect(() => {
    if (G.isNullable(companyId)) {
      setValue('is_international', false);
      return;
    }

    setValue('is_international', isInternationalCompany);
  }, [companyId, isInternationalCompany, setValue]);

  useEffect(() => {
    if (from !== 'early' && G.isNotNullable(earlyReason)) {
      setValue('early_reason', null);
    }
  }, [from, earlyReason, setValue]);

  useEffect(() => {
    if (isInternationalCompany && earlyReason === 'option_exercise') {
      setValue('early_reason', null, { shouldValidate: true });
    }
  }, [isInternationalCompany, earlyReason, setValue]);

  const earlyReasonChoices = useMemo(
    () =>
      isInternationalCompany
        ? b2bWithdrawalEarlyReasonChoices.filter((choice) => choice.id !== 'option_exercise')
        : b2bWithdrawalEarlyReasonChoices,
    [isInternationalCompany],
  );

  useEffect(() => {
    if (!lockedFunds) {
      return;
    }

    setValue('locked_funds', lockedFunds);
  }, [lockedFunds, setValue]);

  useEffect(() => {
    setValue('eligible_allocations', eligibleAllocations);
  }, [eligibleAllocations, setValue]);

  const hasFunds = useMemo(
    () => G.isNotNullable(lockedFunds) && lockedFunds.total > 0,
    [lockedFunds],
  );

  const hasSuspendedFunds =
    G.isNotNullable(lockedFunds) && A.isNotEmpty(lockedFunds.suspended_positions);

  const plansChoices = useMemo(() => {
    if (G.isNullable(lockedFunds)) {
      return [];
    }

    const choices: RadioButtonGroupInputChoices = [
      {
        id: 'available',
        name: 'resources.bo_common.b2b.withdrawals.create.labels.available_funds',
        subtitle: 'resources.bo_common.b2b.withdrawals.create.subtitles.available_out',
        options: {
          available: lockedFunds.withdrawable / 100,
          total: lockedFunds.total / 100,
        },
      },
      {
        id: 'early',
        name: 'resources.bo_common.b2b.withdrawals.create.labels.early_request',
        subtitle: 'resources.bo_common.b2b.withdrawals.create.subtitles.available_locked',
        options: {
          available: lockedFunds.locked / 100,
        },
      },
    ];

    return choices;
  }, [lockedFunds]);

  const clearEmployee = useCallback(() => {
    setValue('firstname', null);
    setValue('lastname', null);
    setValue('social_status', null);
    setValue('nir', null);
    setValue('ntt', null);
    setValue('user_id', null);
    setValue('beneficiary_id', null);
    setB2bEmployee(null);
  }, [setValue, setB2bEmployee]);

  useEffect(() => {
    if (G.isNullable(employeeId)) {
      clearEmployee();
    }
  }, [employeeId, clearEmployee]);

  const handleCompanyChange = () => {
    setValue('b2b_employee_id', null);
    clearEmployee();
  };

  const handleEmployeeChange = (id: number | '', record: GetB2bEmployeesOneResponse | '') => {
    if (G.isNullable(id) || id === '' || record === '') {
      clearEmployee();
    } else {
      setValue('firstname', record.firstname);
      setValue('lastname', record.lastname);
      setValue('social_status', record.social_status);
      setValue('nir', record.nir);
      setValue('ntt', record.ntt);
      setValue('user_id', record.user_id);
      setValue('is_deceased', record.is_deceased);
      setValue('beneficiary_id', null);
      setB2bEmployee(record);
    }
  };

  return (
    <>
      <ShowSection
        leftAligned
        label="resources.bo_common.b2b.withdrawals.create.sections.affiliate_information"
        noCard={true}>
        {!isBoExternal() && (
          <B2bCompanySelectInput
            source="b2b_company_id"
            validate={[required()]}
            label="resources.b2b/withdrawals.fields.company_id"
            onChange={handleCompanyChange}
            testId={`${testId}_CompanySelectInput`}
          />
        )}
        <B2bEmployeeSelectInput
          testId={`${testId}_EmployeeSelectInput`}
          source="b2b_employee_id"
          reference={`${resourceBasePath}employees`}
          label="resources.bo_common.b2b.withdrawals.create.labels.b2b_employee_id_search"
          validate={[required()]}
          filter={{ company_id: !G.isNullable(companyId) ? companyId : 0 }}
          shouldRenderSuggestions={() => !G.isNullable(companyId)}
          noOptionsText={translate(
            'resources.bo_common.b2b.withdrawals.create.errors.no_employees_found',
          )}
          onChange={handleEmployeeChange}
          {...inputDisabledProps(G.isNullable(companyId))}
        />
        <RecordContextProvider value={b2bEmployee ?? undefined}>
          <TextInput
            data-testid={`${testId}_FirstnameInput`}
            source="firstname"
            label="resources.bo_common.b2b.employees.fields.firstname"
            {...inputDisabledProps(true)}
          />
          <TextInput
            data-testid={`${testId}_LastnameInput`}
            source="lastname"
            label="resources.bo_common.b2b.employees.fields.lastname"
            {...inputDisabledProps(true)}
          />
          <TextInput
            data-testid={`${testId}_NirInput`}
            source="nir"
            label="resources.bo_common.b2b.employees.fields.nir"
            {...inputDisabledProps(true)}
          />
          <TextInput
            data-testid={`${testId}_NttInput`}
            source="ntt"
            label="resources.bo_common.b2b.employees.fields.ntt"
            {...inputDisabledProps(true)}
          />
          <TextInput
            data-testid={`${testId}_SocialStatusInput`}
            source="social_status"
            label="resources.bo_common.b2b.employees.fields.social_status"
            {...inputDisabledProps(true)}
          />
        </RecordContextProvider>
        {!isBoExternal() && (
          <DuplicateEmployeeNameAlert b2bEmployee={b2bEmployee} testId={testId} />
        )}
      </ShowSection>
      <ShowSection
        leftAligned
        label="resources.bo_common.b2b.withdrawals.create.sections.from"
        subtitle="resources.bo_common.b2b.withdrawals.create.subtitles.from"
        noCard={true}>
        {hasFunds ? (
          <RadioButtonGroupInput
            data-testid={`${testId}_FromInput`}
            source="from"
            choices={plansChoices}
            disabled={G.isNullable(lockedFunds)}
            required={true}
          />
        ) : (
          !hasSuspendedFunds && (
            <TypographyTranslated label="resources.bo_common.b2b.withdrawals.create.labels.no_funds" />
          )
        )}
        {hasSuspendedFunds && <SuspendedFundsNotice lockedFunds={lockedFunds} testId={testId} />}
      </ShowSection>
      {from === 'early' && (
        <>
          <ShowSection
            leftAligned
            label="resources.bo_common.b2b.withdrawals.create.labels.early_request"
            noCard={true}>
            <SelectInput
              data-testid={`${testId}_EarlyReasonSelectInput`}
              source="early_reason"
              label="resources.bo_common.b2b.withdrawals.create.labels.reason"
              choices={earlyReasonChoices}
              validate={[required()]}
              onChange={() => setValue('beneficiary_id', null)}
            />
            <DateInput
              data-testid={`${testId}_DateOfEventInput`}
              source="date_of_event"
              label="resources.bo_common.b2b.withdrawals.create.labels.date_of_event"
              validate={[required(), maxDate(() => toDateString(subDays(new Date(), 2)))]}
            />
          </ShowSection>
          <ShowSection
            leftAligned
            label="resources.bo_common.b2b.withdrawals.create.sections.documents_supporting"
            noCard={true}>
            <FileInput
              data-testid={`${testId}_DocumentsSupportingInput`}
              source="documents_supporting"
              accept="application/pdf,image/png,image/jpeg"
              maxSize={DOCUMENTS_UPLOAD_LARGE_FILE_MAX_SIZE}
              validate={isInternationalCompany ? undefined : required()}
              multiple={true}>
              <FileField
                data-testid={`${testId}_DocumentsSupportingField`}
                source="src"
                title="title"
              />
            </FileInput>
          </ShowSection>
        </>
      )}
    </>
  );
};
